import { useFormContext } from 'react-hook-form';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Plus } from 'lucide-react';
import { NotificationRuleFormData } from '@/features/notification-rules/schemas/create-notification-rule.schema.ts';
import { useGetDevicesQuery } from '@/features/devices/hooks/queries/get-all-devices.ts';
import { useEffect, useMemo } from 'react';
import {
  getDeviceParameterInfo,
  getMessageExamples,
  getPlantParameterInfo,
} from '@/features/notification-rules/schemas/notification-rules.utils.ts';

type MessageTemplateFields = Pick<
  NotificationRuleFormData,
  | 'ruleType'
  | 'deviceType'
  | 'plantId'
  | 'deviceId'
  | 'conditions'
  | 'notificationTemplate'
  | 'notificationPreviewTemplate'
>;

export function NotificationRuleMessageTemplateForm({
  showTitle = true,
}: {
  showTitle?: boolean;
}) {
  const form = useFormContext<MessageTemplateFields>();
  const watchRuleType = form.watch('ruleType');
  const watchDeviceType = form.watch('deviceType');
  const watchPlantId = form.watch('plantId');
  const watchDeviceId = form.watch('deviceId');
  const watchConditions = form.watch('conditions');
  const notificationTemplate = form?.watch('notificationTemplate');

  const messageExamples = useMemo(() => getMessageExamples(watchRuleType), [watchRuleType]);

  const { data: devices } = useGetDevicesQuery();
  const selectedDevice = devices?.find((p) => p.deviceId === watchDeviceId);

  const { data: plants } = useGetPlantsQuery({ includeOverview: false });
  const selectedPlant = plants?.find((p) => p.plantId === watchPlantId);

  const availableVariables = useMemo(() => {
    const baseVariables = [
      { variable: '{{timestamp}}', description: 'Current date and time' },
      { variable: '{{plantName}}', description: 'Name of the plant' },
    ];

    if (watchRuleType === 'device') {
      baseVariables.push(
        { variable: '{{deviceType}}', description: 'Type of device' },
        { variable: '{{deviceSN}}', description: 'Device serial number' }
      );
    }

    if (watchRuleType === 'schedule') {
      baseVariables.push({
        variable: '{{setpointValue}}',
        description: 'Power setpoint value for the active window',
      });
    }

    const conditionVariables =
      watchConditions?.flatMap((condition, index) => {
        const paramInfo =
          watchRuleType === 'device'
            ? getDeviceParameterInfo(watchDeviceType, condition, index)
            : getPlantParameterInfo(condition, index);

        if (!paramInfo) return [];

        return [
          { variable: paramInfo.fact, description: paramInfo.paramName },
          {
            variable: paramInfo.value,
            description: `Current value of ${paramInfo.paramName}`,
          },
          {
            variable: paramInfo.threshold,
            description: `Threshold value for ${paramInfo.paramName}`,
          },
        ];
      }) || [];

    return [...baseVariables, ...conditionVariables];
  }, [watchRuleType, watchDeviceType, watchConditions]);

  const insertVariable = (variable: string) => {
    const currentValue = form.getValues('notificationTemplate') ?? '';
    const newValue = currentValue + variable;
    form.setValue('notificationTemplate', `${newValue} `);
  };

  const previewText = useMemo(() => {
    if (!notificationTemplate) return '';

    const replacements: {
      [key in string]: string;
    } = {
      '{{plantName}}': selectedPlant?.basicDetails?.name || '[Plant Name]',
      '{{deviceType}}': watchDeviceType || '[Device Type]',
      '{{deviceSN}}': selectedDevice?.sn || '[Device SN]',
      '{{timestamp}}': new Date().toLocaleString(),
    };

    if (watchConditions)
      for (const [index, condition] of watchConditions.entries()) {
        const paramInfo =
          watchRuleType === 'device'
            ? getDeviceParameterInfo(watchDeviceType, condition, index)
            : getPlantParameterInfo(condition, index);

        if (paramInfo) {
          replacements[paramInfo.fact] = paramInfo.paramName;
          replacements[paramInfo.value] = `[${paramInfo.paramName} Value]`;
          replacements[paramInfo.threshold] =
            paramInfo.thresholdValue || condition.value || '[Threshold Value]';
        }
      }

    return Object.entries(replacements).reduce(
      (text, [variable, replacement]) => text.replaceAll(variable, replacement),
      notificationTemplate
    );
  }, [
    notificationTemplate,
    selectedPlant,
    watchDeviceType,
    selectedDevice,
    watchConditions,
    watchRuleType,
  ]);

  useEffect(() => {
    form.setValue('notificationPreviewTemplate', previewText);
  }, [previewText, form]);

  return (
    <div className="space-y-2">
      {showTitle && (
        <div>
          <h2 className="mb-2 font-semibold">Notification Description</h2>
        </div>
      )}

      <FormField
        control={form.control}
        name="notificationTemplate"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="required">Description Template</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Enter your message template here..."
                className="min-h-[100px] resize-none"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <h3 className="mb-2 text-sm font-medium">Available Variables</h3>
          <div className="max-h-40 overflow-y-auto rounded-md border p-3">
            <div className="space-y-1">
              {availableVariables?.map((item, index) => (
                <div
                  key={index}
                  className="hover:bg-muted group flex cursor-pointer items-center justify-between rounded px-2 py-1"
                  onClick={() => insertVariable(item.variable)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono text-xs text-blue-600">
                      {item.variable}
                    </div>
                    <div className="text-muted-foreground truncate text-xs">
                      {item.description}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-2 h-6 w-6 flex-shrink-0 p-0 transition-opacity sm:opacity-0 sm:group-hover:opacity-100"
                  >
                    <Plus size={12} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-medium">Example Templates</h3>
          <div className="max-h-40 overflow-y-auto rounded-md border p-3">
            <div className="space-y-2">
              {messageExamples?.map((example, index) => (
                <div key={index} className="group">
                  <div className="mb-1 flex items-center justify-between">
                    <h4 className="text-xs font-medium">{example.title}</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => form.setValue('notificationTemplate', example.template)}
                      className="mr-2 h-6 px-2 text-xs transition-opacity sm:mr-0 sm:opacity-0 sm:group-hover:opacity-100"
                    >
                      <Plus size={12} />
                    </Button>
                  </div>
                  <p className="text-muted-foreground bg-muted/50 rounded p-2 font-mono text-xs text-wrap break-words">
                    {example.template}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-medium">Preview</h3>
        <div className="bg-muted rounded-md border p-3">
          <p className="min-h-5 text-sm">{previewText}</p>
        </div>
      </div>
    </div>
  );
}
